import { MenuProps, ThemeProvider } from '@mui/material';
import * as React from 'react';
import { memo, useCallback, useState } from 'react';
import { createContext } from 'use-context-selector';

import useKeypress from '../../../hooks/useKeyPress';
import { lightTheme } from '../../../theme';
import { defaultMenuProps, StyledMenu } from './PopoverMenu.style';

export interface PopoverMenuInjectedProps {
    close: () => void;
    opened: boolean;
}

export const PopoverMenuContext = createContext<PopoverMenuInjectedProps>({
    close: () => {
        /**/
    },
    opened: false,
} as any);

export const PopoverMenu = memo(function PopoverMenu({
    renderButton,
    disableBackdropClose,
    autoClose,
    children,
    menuProps,
    onClose,
}: {
    renderButton: (
        props: {
            onClick: (e: React.MouseEvent<HTMLElement>) => void;
        } & PopoverMenuInjectedProps,
    ) => React.ReactNode;
    autoClose?: boolean;
    disableBackdropClose?: boolean;
    children?: React.ReactNode | ((options: PopoverMenuInjectedProps) => React.ReactNode);
    menuProps?: Partial<MenuProps>;
    onClose?: () => void;
}) {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    }, []);

    const handleClose = useCallback(() => {
        setAnchorEl(null);
        onClose?.();
    }, [onClose]);

    useKeypress('Escape', open && handleClose);

    const state: PopoverMenuInjectedProps = { close: handleClose, opened: open };

    return (
        <PopoverMenuContext.Provider value={state}>
            {renderButton({ onClick: handleClick, ...state })}
            {children && (
                <ThemeProvider theme={lightTheme}>
                    <StyledMenu
                        disablePortal
                        elevation={0}
                        {...defaultMenuProps}
                        anchorEl={anchorEl}
                        open={open}
                        onClose={disableBackdropClose ? undefined : handleClose}
                        onClick={autoClose ? handleClose : undefined}
                        {...menuProps}
                    >
                        {typeof children === 'function' ? children(state) : children}
                    </StyledMenu>
                </ThemeProvider>
            )}
        </PopoverMenuContext.Provider>
    );
});
